import React, { useState, useMemo, useEffect } from 'react';
import { countries as countriesJSON } from 'data';

import {
  DateInputWithConfirm,
  InputWithConfirm,
  AutocompleteInputWithConfirm,
  RadioOptionsWithConfirm,
  IQuestion,
  getStringFromDate,
  getDateFromString,
  ContinueButton,
  isValidDate,
} from 'shared-components';

import { VQ } from '../../../../../store/student/types';
import { IDate, ITravelItems } from '../../../../../types';
import { useTranslation } from "react-i18next";

interface IProps {
  index: number;

  country: string;
  reason: string;
  reasonOther: string;
  entryDate: string;
  exitDate: string;

  onSaveAndClose: (travel: ITravelItems) => void;
}

const TravelItems = (props: IProps) => {
    const { t } = useTranslation('common');
  const { index, onSaveAndClose } = props;
  const [country, setCountry] = useState(props.country);
  const [reason, setReason] = useState(props.reason);
  const [reasonOther, setReasonOther] = useState(props.reasonOther);
  const [dEntry, setDEntry] = useState(() => getDateFromString(props.entryDate));
  const [dEntryError, setDEntryError] = useState('');
  const [dExit, setDExit] = useState(() => getDateFromString(props.exitDate));
  const [dExitError, setDExitError] = useState('');

  const countries = useMemo(() => [...new Set(countriesJSON.map(country => country.countryName))], []);
  const [countrySuggestions, setCountrySuggestions] = useState(countries);

  const saveAndClose = () => {
    onSaveAndClose({
      country,
      reason,
      reasonOther,
      entryDate: getStringFromDate(dEntry),
      exitDate: getStringFromDate(dExit),
    });
  };

  const handleOnSaveAndCloseClick = (_event: React.MouseEvent<HTMLButtonElement>) => {
    saveAndClose();
  };

  const validateDate = (dateValue: IDate): string => {
    const { YYYY, DD, MM } = dateValue;

    if (YYYY && DD && MM) {
      const isValid = isValidDate(dateValue);
      if (!isValid) {
        return "Doesn't look like a valid date";
      }
      return '';
    }

    return '';
  };

  const onBlurCountry = (event: React.FormEvent<HTMLInputElement>) => {
    const value = (event.currentTarget.value || '').trim();
    const country = countries.includes(value) ? value : '';

    setCountry(country);
    setCountrySuggestions(countries);
  };

  const onCountryChange = (evt: any) => {
    const value = evt.currentTarget.value || '';
    const suggestions = countries.filter((country: string) => country.toLowerCase().includes(value.toLowerCase()));

    setCountry(value);
    setCountrySuggestions(suggestions);
  };

  const complete =
    country !== '' &&
    reason !== '' &&
    (reason === 'Other - provide details' ? reasonOther !== '' : true) &&
    dEntry.DD !== null &&
    dEntry.MM !== null &&
    dEntry.YYYY !== null &&
    dEntryError === '' &&
    dExit.DD !== null &&
    dExit.MM !== null &&
    dExit.YYYY !== null &&
    dExitError === '';

  useEffect(() => {
    if (reason !== 'Other - provide details') setReasonOther('');
  }, [reason]);

  return (
    <>
      <AutocompleteInputWithConfirm
        key={`${VQ.TRAVEL_DETAILS_COUNTRY}-${index}`}
        id={`${VQ.TRAVEL_DETAILS_COUNTRY}-${index}`}
        label={t('common:country')}
        onChange={(evt: any) => onCountryChange(evt)}
        value={country}
        suggestions={countrySuggestions}
        onBlur={(evt: any) => onBlurCountry(evt)}
      />

      <RadioOptionsWithConfirm
        key={`${VQ.TRAVEL_DETAILS_REASON}-${index}`}
        id={`${VQ.TRAVEL_DETAILS_REASON}-${index}`}
        label={t('common:reasonForTravel')}
        onClick={(question: IQuestion) => setReason(question.label)}
        questions={[
          {
            char: '1',
            label: 'Tourism (including visiting family or friends)',
            selected: reason === 'Tourism (including visiting family or friends)',
          },
          {
            char: '2',
            label: 'Work',
            selected: reason === 'Work',
          },
          {
            char: '3',
            label: 'Study',
            selected: reason === 'Study',
          },
          {
            char: '4',
            label: 'Transit (travelling through the country)',
            selected: reason === 'Transit (travelling through the country)',
          },
          {
            char: '5',
            label: 'Other - provide details',
            selected: reason === 'Other - provide details',
          },
        ]}
      />

      {reason === 'Other - provide details' ? (
        <InputWithConfirm
          key={`${VQ.TRAVEL_DETAILS_REASON_OTHER}-${index}`}
          id={`${VQ.TRAVEL_DETAILS_REASON_OTHER}-${index}`}
          value={reasonOther}
          label={t('common:reasonForTravelOther')}
          onChange={(evt: any) => {
            setReasonOther(evt.currentTarget.value);
          }}
        />
      ) : null}

      <DateInputWithConfirm
        key={`${VQ.TRAVEL_DETAILS_ENTRY_DATE}-${index}`}
        id={`${VQ.TRAVEL_DETAILS_ENTRY_DATE}-${index}`}
        label={t('common:whenDidYouEnterThisCountry')}
        value={dEntry}
        errorText={dEntryError}
        onChangeDD={(evt: any) => setDEntry({ ...dEntry, DD: evt.target.value })}
        onChangeMM={(evt: any) => setDEntry({ ...dEntry, MM: evt.target.value })}
        onChangeYY={(evt: any) => setDEntry({ ...dEntry, YYYY: evt.target.value })}
        onBlur={(_evt: any) => setDEntryError(validateDate(dEntry))}
      />

      <DateInputWithConfirm
        key={`${VQ.TRAVEL_DETAILS_EXIT_DATE}-${index}`}
        id={`${VQ.TRAVEL_DETAILS_EXIT_DATE}-${index}`}
        label={t('common:whenDidYouLeaveThisCountry')}
        value={dExit}
        errorText={dExitError}
        onChangeDD={(evt: any) => setDExit({ ...dExit, DD: evt.target.value })}
        onChangeMM={(evt: any) => setDExit({ ...dExit, MM: evt.target.value })}
        onChangeYY={(evt: any) => setDExit({ ...dExit, YYYY: evt.target.value })}
        onBlur={(_evt: any) => setDExitError(validateDate(dExit))}
      />

      <ContinueButton index={index} complete={complete} handleOnSaveAndCloseClick={handleOnSaveAndCloseClick} />
    </>
  );
};

export default TravelItems;
